import * as React from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "../lib/utils";
import { useCopyToClipboard } from "../hooks/use-copy-to-clipboard";

/* ------------------------------------------------------------------ */
/* Tiny hand-rolled tokenizer — no highlight deps installed, so we     */
/* ship a small sticky-regex scanner for bash / json / js/ts / sql.    */
/* Colours reuse existing tokens only.                                 */
/* ------------------------------------------------------------------ */

export type CodeLanguage = "bash" | "shell" | "json" | "js" | "jsx" | "ts" | "tsx" | "sql" | "text";

interface Token {
  text: string;
  cls: string;
}

const T = {
  comment: "text-muted-foreground italic",
  string: "text-success",
  number: "text-warning",
  keyword: "text-primary font-medium",
  fn: "text-accent",
  variable: "text-code-foreground font-semibold",
  plain: "",
};

const TS_KEYWORDS =
  /^(abstract|as|async|await|break|case|catch|class|const|continue|debugger|default|delete|do|else|enum|export|extends|finally|for|from|function|get|if|implements|import|in|instanceof|interface|keyof|let|namespace|new|of|private|protected|public|readonly|return|satisfies|set|static|switch|this|throw|try|type|typeof|undefined|unique|var|void|while|with|yield|null|true|false)$/;

const SQL_KEYWORDS =
  /^(select|from|where|insert|into|values|update|set|delete|create|alter|drop|table|index|view|join|inner|left|right|full|outer|on|group|by|order|limit|offset|having|union|all|distinct|as|and|or|not|null|is|in|exists|between|like|case|when|then|else|end|primary|key|foreign|references|unique|default|constraint|begin|commit|rollback|transaction|with|returning|desc|asc)$/i;

const BASH_KEYWORDS =
  /^(if|then|fi|elif|else|for|while|until|do|done|case|esac|function|return|break|continue|export|source|alias|unset|local|readonly|in)$/;

const BASH_CMDS =
  /^(echo|cd|ls|cat|grep|sed|awk|curl|wget|chmod|chown|mkdir|rm|mv|cp|touch|npm|npx|pnpm|yarn|node|git|docker|kubectl|asrt|sudo|apt|brew|ssh|scp|env|make|cargo|python|pip)$/;

interface Rule {
  re: RegExp;
  cls: (m: string, after: string) => string;
}

function wordRules(
  keyword: RegExp,
  fnColor: boolean,
): Rule[] {
  return [
    {
      // identifier followed by "(" → function call
      re: /[A-Za-z_$][\w$-]*(?=\s*\()/y,
      cls: (m) => (keyword.test(m) ? T.keyword : fnColor ? T.fn : T.plain),
    },
    {
      re: /[A-Za-z_$][\w$-]*/y,
      cls: (m) => (keyword.test(m) ? T.keyword : T.plain),
    },
  ];
}

const LANG_RULES: Partial<Record<CodeLanguage, Rule[]>> = {
  bash: [
    { re: /#.*/y, cls: () => T.comment },
    { re: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/y, cls: () => T.string },
    { re: /\$\{[^}]*\}|\$\w+/y, cls: () => T.variable },
    { re: /(?:^|(?<=[\s|;]))-{1,2}[A-Za-z][\w-]*/y, cls: () => T.fn },
    { re: /\b\d[\d._]*\b/y, cls: () => T.number },
    {
      re: /[A-Za-z_$][\w-]*(?=\s*\()/y,
      cls: (m) => (BASH_KEYWORDS.test(m) ? T.keyword : T.plain),
    },
    {
      re: /[A-Za-z_$][\w-]*/y,
      cls: (m) => (BASH_KEYWORDS.test(m) ? T.keyword : BASH_CMDS.test(m) ? T.fn : T.plain),
    },
  ],
  shell: [],
  json: [
    { re: /"(?:\\.|[^"\\])*"(?=\s*:)/y, cls: () => T.keyword },
    { re: /"(?:\\.|[^"\\])*"/y, cls: () => T.string },
    { re: /\b(?:true|false|null)\b/y, cls: () => T.variable },
    { re: /-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/y, cls: () => T.number },
  ],
  js: [
    { re: /\/\/.*|\/\*[\s\S]*?\*\//y, cls: () => T.comment },
    { re: /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`/y, cls: () => T.string },
    { re: /\b0[xo][\da-fA-F_]+\b|\b\d[\d._]*(?:e[+-]?\d+)?n?\b/y, cls: () => T.number },
    ...wordRules(TS_KEYWORDS, true),
  ],
  sql: [
    { re: /--.*|\/\*[\s\S]*?\*\//y, cls: () => T.comment },
    { re: /'(?:''|[^'])*'/y, cls: () => T.string },
    { re: /\b\d[\d._]*\b/y, cls: () => T.number },
    ...wordRules(SQL_KEYWORDS, false),
  ],
  text: [],
};

LANG_RULES.shell = LANG_RULES.bash;
LANG_RULES.jsx = LANG_RULES.js;
LANG_RULES.ts = LANG_RULES.js;
LANG_RULES.tsx = LANG_RULES.js;

function tokenize(code: string, language: CodeLanguage): Token[] {
  const rules = LANG_RULES[language] ?? [];
  const tokens: Token[] = [];
  let i = 0;

  const push = (text: string, cls: string) => {
    const last = tokens[tokens.length - 1];
    if (last && last.cls === cls) last.text += text;
    else tokens.push({ text, cls });
  };

  while (i < code.length) {
    let matched = false;
    for (const rule of rules) {
      rule.re.lastIndex = i;
      const m = rule.re.exec(code);
      if (m && m[0].length > 0) {
        push(m[0], rule.cls(m[0], code.slice(i + m[0].length)));
        i += m[0].length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      push(code[i], T.plain);
      i += 1;
    }
  }
  return tokens;
}

/* ------------------------------------------------------------------ */
/* Kbd + CopyButton                                                    */
/* ------------------------------------------------------------------ */

const Kbd = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <kbd
      ref={ref}
      className={cn(
        "inline-flex h-5 min-w-[1.25rem] items-center justify-center gap-1 rounded-md border border-border bg-secondary px-1.5 font-mono text-[11px] font-medium text-secondary-foreground shadow-none",
        className,
      )}
      {...props}
    />
  ),
);
Kbd.displayName = "Kbd";

export interface CopyButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text: string;
  label?: string;
  copiedLabel?: string;
  variant?: "ghost" | "outline";
}

const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  ({ text, label, copiedLabel = "Copied", className, variant = "ghost", ...props }, ref) => {
    const { copied, copy } = useCopyToClipboard();
    return (
      <button
        ref={ref}
        type="button"
        onClick={(e) => {
          props.onClick?.(e);
          void copy(text);
        }}
        aria-live="polite"
        className={cn(
          "inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          variant === "ghost"
            ? "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"
            : "border border-input bg-background hover:bg-secondary",
          className,
        )}
        {...props}
      >
        {copied ? (
          <Check className="size-3.5 text-success" aria-hidden="true" />
        ) : (
          <Copy className="size-3.5" aria-hidden="true" />
        )}
        {label ? <span>{copied ? copiedLabel : label}</span> : null}
        <span className="sr-only">{copied ? "Copied to clipboard" : "Copy to clipboard"}</span>
      </button>
    );
  },
);
CopyButton.displayName = "CopyButton";

/* ------------------------------------------------------------------ */
/* CodeBlock                                                           */
/* ------------------------------------------------------------------ */

export interface CodeBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  code: string;
  language?: CodeLanguage;
  filename?: string;
  showLineNumbers?: boolean;
  /** Show the language chip in the header. Default true. */
  showLanguage?: boolean;
  copyable?: boolean;
  maxHeightClassName?: string;
}

const CodeBlock = React.forwardRef<HTMLDivElement, CodeBlockProps>(
  (
    {
      code,
      language = "text",
      filename,
      showLineNumbers = false,
      showLanguage = true,
      copyable = true,
      maxHeightClassName = "max-h-[32rem]",
      className,
      ...props
    },
    ref,
  ) => {
    const tokens = React.useMemo(() => tokenize(code.replace(/\n$/, ""), language), [code, language]);
    const lineCount = Math.max(1, code.replace(/\n$/, "").split("\n").length);

    return (
      <div
        ref={ref}
        className={cn(
          "overflow-hidden rounded-xl border border-border bg-code text-code-foreground",
          className,
        )}
        {...props}
      >
        {filename || showLanguage || copyable ? (
          <div className="flex h-10 items-center gap-3 border-b border-code-foreground/10 px-4">
            <span aria-hidden="true" className="flex gap-1.5">
              <span className="size-2.5 rounded-full bg-destructive/70" />
              <span className="size-2.5 rounded-full bg-warning/70" />
              <span className="size-2.5 rounded-full bg-success/70" />
            </span>
            {filename ? (
              <span className="truncate font-mono text-xs text-code-foreground/80">{filename}</span>
            ) : (
              <span className="flex-1" />
            )}
            <span className="ml-auto flex items-center gap-2">
              {showLanguage && language !== "text" ? (
                <span className="rounded-md bg-code-foreground/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-code-foreground/70">
                  {language}
                </span>
              ) : null}
              {copyable ? <CopyButton text={code} className="text-code-foreground/70 hover:bg-code-foreground/10 hover:text-code-foreground" /> : null}
            </span>
          </div>
        ) : null}
        <div className={cn("flex overflow-auto", maxHeightClassName)}>
          {showLineNumbers ? (
            <div
              aria-hidden="true"
              className="select-none border-r border-code-foreground/10 px-3 py-3 text-right font-mono text-[13px] leading-6 text-code-foreground/40"
            >
              {Array.from({ length: lineCount }, (_, n) => (
                <div key={n}>{n + 1}</div>
              ))}
            </div>
          ) : null}
          <pre className="m-0 flex-1 overflow-visible px-4 py-3 font-mono text-[13px] leading-6">
            <code>
              {tokens.map((tok, i) =>
                tok.cls ? (
                  <span key={i} className={tok.cls}>
                    {tok.text}
                  </span>
                ) : (
                  <React.Fragment key={i}>{tok.text}</React.Fragment>
                ),
              )}
            </code>
          </pre>
        </div>
      </div>
    );
  },
);
CodeBlock.displayName = "CodeBlock";

/* ------------------------------------------------------------------ */
/* Terminal — fake-interactive shell block                             */
/* ------------------------------------------------------------------ */

export interface TerminalProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Command that appears typed after the prompt. */
  command: string;
  prompt?: string;
  output?: React.ReactNode[];
  title?: string;
  /** Blinking caret after the command. Default true. */
  caret?: boolean;
  "aria-label"?: string;
}

const Terminal = React.forwardRef<HTMLDivElement, TerminalProps>(
  ({ className, command, prompt = "asrt ~ $", output = [], title, caret = true, ...aria }, ref) => {
    return (
      <div
        ref={ref}
        role="region"
        aria-label={aria["aria-label"] ?? `Terminal: ${command}`}
        className={cn(
          "overflow-hidden rounded-xl border border-border bg-code text-code-foreground",
          className,
        )}
      >
        <div className="flex h-10 items-center gap-3 border-b border-code-foreground/10 px-4">
          <span aria-hidden="true" className="flex gap-1.5">
            <span className="size-2.5 rounded-full bg-destructive/70" />
            <span className="size-2.5 rounded-full bg-warning/70" />
            <span className="size-2.5 rounded-full bg-success/70" />
          </span>
          <span className="mx-auto -translate-x-4 font-mono text-xs text-code-foreground/60">
            {title ?? "bash"}
          </span>
        </div>
        <div className="px-4 py-3 font-mono text-[13px] leading-6">
          <div className="flex flex-wrap gap-2">
            <span aria-hidden="true" className="select-none text-success">
              {prompt}
            </span>
            <span>
              {command}
              {caret ? (
                <span
                  aria-hidden="true"
                  className="ml-0.5 inline-block h-[1.1em] w-[0.55em] translate-y-[0.15em] bg-code-foreground/80 motion-safe:animate-caret"
                />
              ) : null}
            </span>
          </div>
          {output.length > 0 ? (
            <div aria-live="polite" className="mt-1 space-y-0.5 text-code-foreground/85">
              {output.map((line, i) =>
                typeof line === "string" ? (
                  <div key={i} className={cn(line.startsWith("✗") && "text-destructive", line.startsWith("✓") && "text-success")}>
                    {line || "\u00A0"}
                  </div>
                ) : (
                  <div key={i}>{line}</div>
                ),
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);
Terminal.displayName = "Terminal";

export { CodeBlock, Terminal, Kbd, CopyButton, tokenize, T as tokenClasses };
